import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { IntakeSlideTransition } from '../components/intake/IntakeSlideTransition';
import { LanguageSelectSlide } from '../components/intake/LanguageSelectSlide';
import { IntakeIntroSlide } from '../components/intake/IntakeIntroSlide';
import { IntakeQuestionSlide } from '../components/intake/IntakeQuestionSlide';
import { LumpRewardSlide } from '../components/intake/LumpRewardSlide';
import { ContinueSlide } from '../components/intake/ContinueSlide';
import { LocationQuestionSlide } from '../components/intake/LocationQuestionSlide';
import { LocationRewardSlide } from '../components/intake/LocationRewardSlide';
import { DurationQuestionSlide } from '../components/intake/DurationQuestionSlide';
import { DurationRewardSlide } from '../components/intake/DurationRewardSlide';
import { CompletionSlide } from '../components/intake/CompletionSlide';
import { HcpProblemSlide } from '../components/intake/HcpProblemSlide';
import { HcpSignalsSlide } from '../components/intake/HcpSignalsSlide';
import { HcpGroupingSlide } from '../components/intake/HcpGroupingSlide';
import { HcpRoleViewSlide } from '../components/intake/HcpRoleViewSlide';
import { HcpWorkflowSlide } from '../components/intake/HcpWorkflowSlide';
import { HcpExampleSlide } from '../components/intake/HcpExampleSlide';
import { getText, type Lang } from '../components/intake/translations';
type Step = 'language' | 'intro' | 'q1' | 'reward' | 'q_location' | 'r_location' | 'q_duration' | 'r_duration' | 'q_investigations' | 'completion' | 'hcp_problem' | 'hcp_signals' | 'hcp_grouping' | 'hcp_role' | 'hcp_workflow' | 'hcp_example' | 'hcp_entry' | 'hcp_clusters';
export function BreastClinicIntake() {
  const [currentStep, setCurrentStep] = useState<Step>('language');
  const [direction, setDirection] = useState(1);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const lang = (answers.language || 'en') as Lang;
  const getNextStep = (step: Step, currentAnswers: Record<string, string>): Step => {
    switch (step) {
      case 'language':
        return 'intro';
      case 'intro':
        return 'q1';
      case 'q1':
        return currentAnswers['q1'] === 'Breast lump' ? 'reward' : 'q_investigations';
      case 'reward':
        return 'q_location';
      case 'q_location':
        return 'r_location';
      case 'r_location':
        return 'q_duration';
      case 'q_duration':
        return 'r_duration';
      case 'r_duration':
        return 'q_investigations';
      case 'q_investigations':
        return 'completion';
      case 'completion':
        return 'hcp_problem';
      case 'hcp_problem':
        return 'hcp_signals';
      case 'hcp_signals':
        return 'hcp_grouping';
      case 'hcp_grouping':
        return 'hcp_role';
      case 'hcp_role':
        return 'hcp_workflow';
      case 'hcp_workflow':
        return 'hcp_example';
      case 'hcp_example':
        return 'hcp_example';
    }
  };
  const getPrevStep = (step: Step, currentAnswers: Record<string, string>): Step => {
    switch (step) {
      case 'language':
        return 'language';
      case 'intro':
        return 'language';
      case 'q1':
        return 'intro';
      case 'reward':
        return 'q1';
      case 'q_location':
        return 'reward';
      case 'r_location':
        return 'q_location';
      case 'q_duration':
        return 'r_location';
      case 'r_duration':
        return 'q_duration';
      case 'q_investigations':
        return currentAnswers['q1'] === 'Breast lump' ? 'r_duration' : 'q1';
      case 'completion':
        return 'q_investigations';
      case 'hcp_problem':
        return 'completion';
      case 'hcp_signals':
        return 'hcp_problem';
      case 'hcp_grouping':
        return 'hcp_signals';
      case 'hcp_role':
        return 'hcp_grouping';
      case 'hcp_workflow':
        return 'hcp_role';
      case 'hcp_example':
        return 'hcp_workflow';
    }
  };
  const handleNext = () => {
    const next = getNextStep(currentStep, answers);
    if (next !== currentStep) {
      setDirection(1);
      setCurrentStep(next);
    }
  };
  const handleBack = () => {
    const prev = getPrevStep(currentStep, answers);
    if (prev !== currentStep) {
      setDirection(-1);
      setCurrentStep(prev);
    }
  };
  const handleAnswer = (questionId: string, answer: string) => {
    const newAnswers = {
      ...answers,
      [questionId]: answer
    };
    setAnswers(newAnswers);
    const next = getNextStep(currentStep, newAnswers);
    if (next !== currentStep) {
      setDirection(1);
      setCurrentStep(next);
    }
  };
  const handleFinish = () => {
    setDirection(1);
    setCurrentStep('hcp_problem');
  };
  const handleReset = () => {
    setDirection(-1);
    setCurrentStep('language');
    setAnswers({});
  };
  const isDark = currentStep.startsWith('hcp');
  return <div className="w-full h-[100dvh] max-h-[100dvh] bg-slate-200 flex items-center justify-center overflow-hidden font-sans" data-id="element-720">
      {/* Mobile container constraint */}
      <div className="w-full h-full max-w-md bg-slate-50 relative overflow-hidden shadow-2xl" data-id="element-721">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-722">
          <IntakeSlideTransition key={currentStep} direction={direction} isDark={isDark} data-id="element-723">
            {currentStep === 'language' && <LanguageSelectSlide onSelect={selectedLang => {
            setAnswers({
              ...answers,
              language: selectedLang
            });
            setDirection(1);
            setCurrentStep('intro');
          }} data-id="element-724" />}

            {currentStep === 'intro' && <IntakeIntroSlide onStart={handleNext} lang={lang} data-id="element-725" />}

            {currentStep === 'q1' && <IntakeQuestionSlide question={getText('q1Question', lang)} options={[getText('breastPain', lang), getText('breastLump', lang), getText('nippleDischarge', lang)]} infoText={getText('q1Info', lang)} backgroundImage="https://images.unsplash.com/photo-1590611936760-eeb9bc598548?q=80&w=1000&auto=format&fit=crop" onSelect={answer => {
            // Map translated answer back to English key for routing logic
            const enMap: Record<string, string> = {
              [getText('breastPain', lang)]: 'Breast pain',
              [getText('breastLump', lang)]: 'Breast lump',
              [getText('nippleDischarge', lang)]: 'Nipple discharge'
            };
            handleAnswer('q1', enMap[answer] || answer);
          }} onBack={handleBack} lang={lang} data-id="element-726" />}

            {currentStep === 'reward' && <LumpRewardSlide onNext={handleNext} onBack={handleBack} lang={lang} data-id="element-727" />}

            {currentStep === 'q_location' && <LocationQuestionSlide onSelect={answer => handleAnswer('q_location', answer)} onBack={handleBack} lang={lang} data-id="element-728" />}

            {currentStep === 'r_location' && <LocationRewardSlide onNext={handleNext} onBack={handleBack} lang={lang} data-id="element-729" />}

            {currentStep === 'q_duration' && <DurationQuestionSlide onSelect={answer => handleAnswer('q_duration', answer)} onBack={handleBack} lang={lang} data-id="element-730" />}

            {currentStep === 'r_duration' && <DurationRewardSlide onNext={handleNext} onBack={handleBack} lang={lang} data-id="element-731" />}

            {currentStep === 'q_investigations' && <IntakeQuestionSlide question={getText('investigationsQuestion', lang)} options={[getText('yes', lang), getText('no', lang), getText('notSure', lang)]} backgroundImage="https://images.unsplash.com/photo-1579684385127-1ef15d508118?q=80&w=1000&auto=format&fit=crop" onSelect={answer => handleAnswer('q_investigations', answer)} onBack={handleBack} lang={lang} data-id="element-732" />}

            {currentStep === 'completion' && <CompletionSlide onFinish={handleFinish} lang={lang} data-id="element-733" />}

            {currentStep === 'hcp_problem' && <HcpProblemSlide onNext={handleNext} data-id="element-734" />}

            {currentStep === 'hcp_signals' && <HcpSignalsSlide onNext={handleNext} onBack={handleBack} data-id="element-735" />}

            {currentStep === 'hcp_grouping' && <HcpGroupingSlide onNext={handleNext} onBack={handleBack} data-id="element-736" />}

            {currentStep === 'hcp_role' && <HcpRoleViewSlide onNext={handleNext} onBack={handleBack} data-id="element-737" />}

            {currentStep === 'hcp_workflow' && <HcpWorkflowSlide onNext={handleNext} onBack={handleBack} data-id="element-738" />}

            {currentStep === 'hcp_example' && <HcpExampleSlide onDone={handleReset} onBack={handleBack} data-id="element-739" />}
          </IntakeSlideTransition>
        </AnimatePresence>
      </div>
    </div>;
}